import { useEffect, useMemo, useState } from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { useCltWizardStore } from '@/stores/useCltWizardStore'
import { useTemplates } from '@/hooks/useCltTemplates'
import {
  SUBSTEPS,
  cellKey,
  type CltMatrix,
  type Labeled,
  type StrategyGroup,
  type SubstepKey,
} from './clt.mock'

const DRAFT_ID = '__draft__'

interface CopyFromColumnPopoverProps {
  targetSubstep: SubstepKey
  targetStrategyId: string
  targetStrategyLabel: string
  milestones: Labeled[]
  children: React.ReactNode
}

/**
 * Lets the user copy a whole column's rules into the target column — from the
 * current draft (either Import/Domestic matrix) OR from any other saved CLT.
 */
export function CopyFromColumnPopover({
  targetSubstep,
  targetStrategyId,
  targetStrategyLabel,
  milestones,
  children,
}: CopyFromColumnPopoverProps) {
  const [open, setOpen] = useState(false)
  const draftMatrices = useCltWizardStore(s => s.matrices)
  const draftStrategyGroups = useCltWizardStore(s => s.strategyGroups)
  const copyColumn = useCltWizardStore(s => s.copyColumn)
  const { data: templates = [] } = useTemplates()

  const sources = useMemo(
    () => [
      {
        id: DRAFT_ID,
        label: 'This template (current)',
        matrices: draftMatrices,
        strategyGroups: draftStrategyGroups,
      },
      ...templates.map(t => ({
        id: t.id,
        label: t.name,
        matrices: t.matrices,
        strategyGroups: t.strategyGroups ?? [],
      })),
    ],
    [draftMatrices, draftStrategyGroups, templates]
  )

  const [sourceId, setSourceId] = useState(DRAFT_ID)
  const [sourceSubstep, setSourceSubstep] = useState<SubstepKey>(targetSubstep)
  const [sourceStrategyId, setSourceStrategyId] = useState(targetStrategyId)

  const selectedSource = sources.find(s => s.id === sourceId) ?? sources[0]
  const sourceGroups: StrategyGroup[] = selectedSource.strategyGroups
  const sourceMatrix: CltMatrix = selectedSource.matrices[sourceSubstep]

  // Reset the draft each time the popover opens.
  useEffect(() => {
    if (!open) return
    setSourceId(DRAFT_ID)
    setSourceSubstep(targetSubstep)
    setSourceStrategyId(targetStrategyId)
  }, [open, targetSubstep, targetStrategyId])

  // Keep the chosen column valid for the selected source's columns.
  useEffect(() => {
    if (!sourceGroups.some(g => g.id === sourceStrategyId)) {
      setSourceStrategyId(sourceGroups[0]?.id ?? '')
    }
  }, [sourceGroups, sourceStrategyId])

  const ruleCount = useMemo(
    () =>
      milestones.filter(m => sourceMatrix[cellKey(m.id, sourceStrategyId)])
        .length,
    [sourceMatrix, sourceStrategyId, milestones]
  )

  const handleCopy = () => {
    copyColumn(
      targetSubstep,
      targetStrategyId,
      sourceMatrix,
      sourceStrategyId,
      milestones
    )
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="center" className="w-[360px] p-0">
        <div className="border-b border-border px-4 py-3">
          <h4 className="text-sm font-semibold text-foreground">Copy column</h4>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Copy a column's rules into{' '}
            <span className="font-medium text-foreground">
              {targetStrategyLabel}
            </span>
            .
          </p>
        </div>

        <div className="space-y-3 px-4 py-3">
          <div className="space-y-1.5">
            <Label className="text-xs">From template</Label>
            <Select value={sourceId} onValueChange={setSourceId}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sources.map(source => (
                  <SelectItem key={source.id} value={source.id}>
                    {source.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">From matrix</Label>
            <Select
              value={sourceSubstep}
              onValueChange={v => setSourceSubstep(v as SubstepKey)}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUBSTEPS.map(sub => (
                  <SelectItem key={sub.key} value={sub.key}>
                    {sub.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">From column</Label>
            <Select
              value={sourceStrategyId}
              onValueChange={setSourceStrategyId}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sourceGroups.map(strategy => (
                  <SelectItem key={strategy.id} value={strategy.id}>
                    {strategy.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs text-muted-foreground">
            {ruleCount === 0
              ? 'This column has no rules — copying will clear the target column.'
              : `${ruleCount} rule${ruleCount === 1 ? '' : 's'} will be copied, overwriting the target column.`}
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleCopy}>
            Copy
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
